/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

/**
 * bidBrain brand theme for Chakra UI — the design-token primitive layer (W1.4 / #679).
 *
 * 1:1 extraction of the signed-off design tokens in the `bidbrain-design` skill
 * (`.claude/skills/bidbrain-design/tokens/*.css`, W1.B / #678). Token layer only — no
 * per-page styling here. Semantic color tokens are theme-aware (`base` / `_dark`) so
 * downstream dark mode (#682) is a color-mode flip, not a re-theme.
 *
 *   Signal Purple #683CD8 · Deep Navy #19155E · Adaptive Teal #1FB8A6 · Warm Amber #D9A441
 *   Interface Gray #F6F7F7 · Success #5BA411 · Error #D43F3A
 *   DM Sans (UI + copy) · DM Mono (IDs, metrics, timestamps)
 */
const FONT_SANS = `'DM Sans', Inter, system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif`;
const FONT_MONO = `'DM Mono', ui-monospace, 'SF Mono', Menlo, Consolas, Monaco, monospace`;

/** Exported for `theme.tokens.test.ts`, which checks that no two tokens share a CSS variable. */
export const config = defineConfig({
  globalCss: {
    html: { h: '100%' },
    '#root': { h: '100%' },
    body: {
      h: '100%',
      minH: '100vh',
      margin: 0,
      overflow: 'hidden',
      bg: 'bg.canvas',
      color: 'fg.default',
      fontFamily: FONT_SANS,
    },
  },
  theme: {
    tokens: {
      colors: {
        // Signal Purple ramp (keep for colorPalette="brand")
        brand: {
          50: { value: '#f0ebfc' },
          100: { value: '#d9ccf7' },
          200: { value: '#b99df0' },
          300: { value: '#9670e9' },
          400: { value: '#7a4fe2' },
          500: { value: '#683CD8' },
          600: { value: '#5230B0' },
          700: { value: '#3d2485' },
          800: { value: '#2b1a5e' },
          900: { value: '#19155E' },
          teal: { value: '#1FB8A6' },
          amber: { value: '#D9A441' },
        },
        // Raw brand + semantic hues (mirror tokens/colors.css)
        signal: { value: '#683CD8' },
        signalDark: { value: '#5230B0' },
        signalInk: { value: '#5230B0' },
        signalOnDark: { value: '#9b7fe8' },
        signalSoft: { value: 'rgba(104, 60, 216, 0.10)' },
        navy: { value: '#19155E' },
        navyDark: { value: '#0F0D3D' },
        teal: { value: '#1FB8A6' },
        tealTint: { value: '#E4F6F3' },
        tealInk: { value: '#147d70' },
        amber: { value: '#D9A441' },
        amberTint: { value: 'rgba(217, 164, 65, 0.12)' },
        amberInk: { value: '#a06400' },
        // Code-block surface (always-dark, both color modes) — #680 Pixels viewer
        codeBg: { value: '#1A1A2E' },
        codeFg: { value: '#E0E0FF' },
        // Draft (unsaved / isDraft) + out-of-range (round exceeds plan) statuses (#680)
        draftAmber: { value: '#F57F17' },
        draftAmberDark: { value: '#f5a623' },
        alertOrange: { value: '#E65100' },
        alertOrangeTint: { value: 'rgba(230, 81, 0, 0.12)' },
        alertOrangeBorder: { value: '#FFD0B0' },
        // The tint, ink and border of success, error and teal live on the semantic tokens only: a base
        // `successTint` shares `--chakra-colors-success-tint` with `success.tint` (`theme.tokens.test.ts`).
        success: { value: '#5BA411' },
        error: { value: '#D43F3A' },
        accentGray: { value: '#A0A4B8' },
        ink: { value: '#1A1A1A' },
        textBody: { value: '#333333' },
        textLight: { value: '#666666' },
        border: { value: '#E0E0E0' },
        interfaceGray: { value: '#F6F7F7' },
        white: { value: '#FFFFFF' },
        // Dark palette (tokens/colors.css dark set)
        darkBg: { value: '#0F1520' },
        darkCard: { value: '#0A1A2F' },
        darkNav: { value: '#19155E' },
        darkText: { value: '#DDE1E6' },
        darkTextMuted: { value: '#8a9bb0' },
        darkCharcoal: { value: '#0F1115' },
      },
      fonts: {
        heading: { value: FONT_SANS },
        body: { value: FONT_SANS },
        mono: { value: FONT_MONO },
      },
      // Type scale (tokens/typography.css) — brand-named keys to avoid clobbering Chakra defaults
      fontSizes: {
        display: { value: '60px' },
        h1: { value: '44px' },
        h2: { value: '32px' },
        h3: { value: '22px' },
        bodyLg: { value: '18px' },
        bodyBase: { value: '16px' },
        bodySm: { value: '13px' },
        codeSm: { value: '13px' },
      },
      fontWeights: {
        light: { value: 300 },
        regular: { value: 400 },
        medium: { value: 500 },
        semibold: { value: 600 },
      },
      lineHeights: {
        display: { value: '1.1' },
        heading: { value: '1.25' },
        body: { value: '1.7' },
      },
      letterSpacings: {
        tight: { value: '-0.02em' },
        snug: { value: '-0.01em' },
        wide: { value: '0.06em' },
      },
      // Spacing scale (tokens/spacing.css) — matches Chakra's 4px base; named aliases for parity
      spacing: {
        xs: { value: '4px' },
        sm: { value: '8px' },
        md: { value: '16px' },
        lg: { value: '24px' },
        xl: { value: '32px' },
        '2xl': { value: '48px' },
      },
      sizes: {
        layoutMax: { value: '1280px' },
        // **The console's page cap** (`LP11`, ruled 2026-09-27): a page fills `main` up to this and
        // centres beyond it. What is bounded is a field, not a page, so this binds only on a wide screen.
        pageMax: { value: '1600px' },
      },
      radii: {
        sm: { value: '4px' },
        md: { value: '6px' },
        lg: { value: '8px' },
        full: { value: '9999px' },
      },
      shadows: {
        sm: { value: '0 1px 2px rgba(0, 0, 0, 0.05)' },
        md: { value: '0 2px 4px rgba(0, 0, 0, 0.08)' },
        lg: { value: '0 4px 12px rgba(0, 0, 0, 0.15)' },
        xl: { value: '0 8px 32px rgba(0, 0, 0, 0.20)' },
        focusRing: { value: '0 0 0 3px rgba(104, 60, 216, 0.15)' },
      },
      // Motion (tokens/motion.css)
      durations: {
        instant: { value: '0ms' },
        fast: { value: '100ms' },
        normal: { value: '200ms' },
        moderate: { value: '300ms' },
        slow: { value: '500ms' },
      },
      easings: {
        standard: { value: 'cubic-bezier(0.4, 0.0, 0.2, 1)' },
        decelerate: { value: 'cubic-bezier(0.0, 0.0, 0.2, 1)' },
        accelerate: { value: 'cubic-bezier(0.4, 0.0, 1.0, 1)' },
        sharp: { value: 'cubic-bezier(0.4, 0.0, 0.6, 1)' },
      },
    },
    // Theme-aware semantic layer — light `base` + `_dark` so dark mode (#682) is a color-mode flip
    semanticTokens: {
      colors: {
        bg: {
          canvas: { value: { base: '{colors.interfaceGray}', _dark: '{colors.darkBg}' } },
          card: { value: { base: '{colors.white}', _dark: '{colors.darkCard}' } },
          nav: { value: { base: '{colors.navy}', _dark: '{colors.darkNav}' } },
          subtle: { value: { base: '{colors.interfaceGray}', _dark: '{colors.darkCharcoal}' } },
        },
        fg: {
          default: { value: { base: '{colors.textBody}', _dark: '{colors.darkText}' } },
          muted: { value: { base: '{colors.textLight}', _dark: '{colors.darkTextMuted}' } },
          title: { value: { base: '{colors.navy}', _dark: '{colors.darkText}' } },
          heading: { value: { base: '{colors.ink}', _dark: '{colors.white}' } },
          onAccent: { value: '{colors.white}' },
        },
        border: {
          default: { value: { base: '{colors.border}', _dark: 'rgba(255, 255, 255, 0.10)' } },
          subtle: { value: { base: '#EDEEF3', _dark: 'rgba(255, 255, 255, 0.06)' } },
        },
        accent: {
          solid: { value: { base: '{colors.signal}', _dark: '{colors.signalOnDark}' } },
          ink: { value: { base: '{colors.signalInk}', _dark: '{colors.signalOnDark}' } },
          /**
           * **The hover surface under a solid button's own text** — added 2026-08-27.
           *
           * `ink` was being used for this at ~88 call sites, and `ink` means two different things
           * by mode: in light it is a *deeper* shade (correct under white text), in dark it is a
           * *lighter* one, because there it is a **foreground** colour for tinted surfaces. So in
           * dark mode hovering either did nothing at all (`accent.ink` is literally
           * `accent.solid`) or made the label harder to read than before the pointer arrived.
           *
           * This role has one requirement the other two cannot state: **hovering never lowers the
           * contrast between the button and its own label.** Both values are existing palette
           * entries — a hover state is not the place to invent a brand colour.
           */
          solidHover: { value: { base: '{colors.signalInk}', _dark: '{colors.signal}' } },
          subtle: { value: { base: '{colors.signalSoft}', _dark: 'rgba(155, 127, 232, 0.16)' } },
        },
        /**
         * **Literal values, not `{colors.successTint}`** (`3f`, found building `LP9`'s pills). A base
         * token `successTint` and this semantic `success.tint` compile to **one** CSS variable,
         * `--chakra-colors-success-tint`, so the reference pointed at itself and resolved to nothing:
         * in light mode every `success.tint`/`ink`/`border` and `error.tint`/`ink`/`border`/`solidHover`
         * in the console rendered transparent or inherited. `theme.tokens.test.ts` fails on the next one.
         */
        success: {
          solid: { value: '{colors.success}' },
          tint: { value: { base: '#E8F5E9', _dark: 'rgba(91, 164, 17, 0.16)' } },
          ink: { value: { base: '#3a7a08', _dark: '#7bc53a' } },
          border: { value: { base: '#C8E6C9', _dark: 'rgba(91, 164, 17, 0.35)' } },
        },
        error: {
          solid: { value: '{colors.error}' },
          /** See `accent.solidHover`. The error ink is deeper than `error` in both modes. */
          solidHover: { value: '#a32d2d' },
          tint: { value: { base: '#FDECEC', _dark: 'rgba(212, 63, 58, 0.18)' } },
          ink: { value: { base: '#a32d2d', _dark: '#e56b66' } },
          border: { value: { base: '#FFCDD2', _dark: 'rgba(212, 63, 58, 0.40)' } },
        },
        warning: {
          solid: { value: '{colors.amber}' },
          tint: { value: { base: '{colors.amberTint}', _dark: 'rgba(217, 164, 65, 0.18)' } },
          ink: { value: { base: '{colors.amberInk}', _dark: '#d9a441' } },
          border: { value: { base: '#FFE58F', _dark: 'rgba(217, 164, 65, 0.40)' } },
        },
        // Informational surfaces — Adaptive Teal family (callouts, info badges). #680
        info: {
          solid: { value: '{colors.teal}' },
          tint: { value: { base: '{colors.tealTint}', _dark: 'rgba(31, 184, 166, 0.16)' } },
          ink: { value: { base: '{colors.tealInk}', _dark: '#4fd0c0' } },
          // Literal for the reason `success` gives: `tealBorder` shares `--chakra-colors-teal-border` with
          // Chakra's own `teal.border`, which points at a `teal.500` this theme does not have.
          border: { value: { base: '#A8E5DD', _dark: 'rgba(31, 184, 166, 0.35)' } },
        },
        // Code-block viewer — intentionally dark in both color modes
        codeBlock: {
          bg: { value: '{colors.codeBg}' },
          fg: { value: '{colors.codeFg}' },
        },
        // Draft status — unsaved / isDraft plans (amber, distinct from warning)
        draft: { value: { base: '{colors.draftAmber}', _dark: '{colors.draftAmberDark}' } },
        // Out-of-range status — a round exceeds the plan's total rounds (deep orange alert)
        outOfRange: {
          solid: { value: { base: '{colors.alertOrange}', _dark: '{colors.draftAmberDark}' } },
          tint: { value: { base: '{colors.alertOrangeTint}', _dark: 'rgba(230, 81, 0, 0.20)' } },
          border: { value: { base: '{colors.alertOrangeBorder}', _dark: 'rgba(230, 81, 0, 0.45)' } },
        },
        neutral: {
          gray: { value: '{colors.accentGray}' },
        },
        // Chakra system semantic overrides (theme-aware)
        'chakra-body-bg': { value: { base: '{colors.interfaceGray}', _dark: '{colors.darkBg}' } },
        'chakra-body-text': { value: { base: '{colors.textBody}', _dark: '{colors.darkText}' } },
        'chakra-border-color': { value: { base: '{colors.border}', _dark: 'rgba(255, 255, 255, 0.10)' } },
        'chakra-placeholder-color': { value: '{colors.accentGray}' },
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);
